import type {TDailyActivity, TDailyReport} from '../types/dashboard.type'
import {getDateFormatted} from './getDateFormatted'

/** Formats a Date as a UTC "YYYY-MM-DD" string. */
export const toDateString = (date: Date): string =>
  date.toISOString().slice(0, 10)

/** Every date of `today`'s month ("YYYY-MM-DD", UTC), 1st → last day, sorted. */
export const getCurrentMonthDates = (today: Date): string[] => {
  const year = today.getUTCFullYear()
  const month = today.getUTCMonth()
  const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate()
  const dates: string[] = []
  for (let day = 1; day <= daysInMonth; day++) {
    dates.push(toDateString(new Date(Date.UTC(year, month, day))))
  }
  return dates
}

/** Keeps the earliest `maxDays` dates of a sorted list, collapsing an over-long span. */
export const clampDateList = (dates: string[], maxDays: number): string[] =>
  dates.slice(0, maxDays)

/** Every-Nth tick step so a band axis shows at most ~8 labels. */
export const computeTickStep = (count: number): number =>
  Math.max(1, Math.ceil(count / 8))

/** "YYYY-MM-DD" → "DD/MM" for compact axis ticks. */
export const formatDayLabel = (dateStr: string): string => {
  const [, month, day] = dateStr.split('-')
  return `${day}/${month}`
}

export type TPieSlice = {
  id: string
  label: string
  value: number
  color: string
}

/**
 * Sorts slices by value descending and folds the tail into an "other" bucket:
 * a slice folds when its share of the total is strictly below `minShare`, or
 * when it ranks beyond the top `maxNamed`. Returns `other: null` when nothing
 * folds so callers never render an empty extra slice.
 */
export const foldSmallSlices = (
  slices: TPieSlice[],
  {minShare, maxNamed}: {minShare: number; maxNamed: number},
): {named: TPieSlice[]; other: {value: number; items: TPieSlice[]} | null} => {
  const total = slices.reduce((acc, s) => acc + s.value, 0)
  if (total === 0) return {named: [], other: null}
  const sorted = [...slices].sort((a, b) => b.value - a.value)
  const named: TPieSlice[] = []
  const folded: TPieSlice[] = []
  sorted.forEach((s, index) => {
    if (index < maxNamed && s.value / total >= minShare) named.push(s)
    else folded.push(s)
  })
  if (folded.length === 0) return {named, other: null}
  return {
    named,
    other: {value: folded.reduce((acc, s) => acc + s.value, 0), items: folded},
  }
}

/**
 * Expands per-day activity counts to cover every date of the selected period:
 * one entry per element of `dates`, in `dates` order, zero-filled when the
 * API returned no row for that day. Entries outside `dates` are dropped —
 * the chart's axis is the selected period, nothing else.
 */
export const fillMissingDays = (
  days: TDailyActivity[],
  dates: string[],
): TDailyActivity[] => {
  const byDate = new Map(days.map(d => [d.date, d]))
  return dates.map(
    date =>
      byDate.get(date) ?? {
        date,
        validated: 0,
        pending: 0,
        deleted: 0,
        cancelled: 0,
      },
  )
}

/** Same contract as `fillMissingDays`, for the per-day report outcome counts. */
export const fillMissingReportDays = (
  days: TDailyReport[],
  dates: string[],
): TDailyReport[] => {
  const byDate = new Map(days.map(d => [d.date, d]))
  return dates.map(
    date =>
      byDate.get(date) ?? {
        date,
        done: 0,
        partial: 0,
        notdone: 0,
        missing: 0,
        upcoming: 0,
      },
  )
}

/**
 * Fixed y-axis width shared by the per-day charts: MUI sizes it from the
 * widest tick label, so two charts with different maxima would otherwise
 * start their bands at different x and the bars would no longer line up.
 */
export const DAY_CHART_Y_AXIS_WIDTH = 40

/** The band axis of one bar per date, identical on every per-day chart so their bars align. */
export const buildDayAxis = (dates: string[]) => {
  // With up to 31 daily bars, show every Nth date label to avoid crowding.
  const step = computeTickStep(dates.length)
  return {
    scaleType: 'band' as const,
    data: dates,
    height: 'auto' as const,
    // Tick under each column: short weekday (e.g. "Lu"); tooltip: full DD/MM.
    valueFormatter: (value: string, context: {location: string}) =>
      context.location === 'tick'
        ? (getDateFormatted(value, 'day', 'medium') ?? '')
        : formatDayLabel(value),
    tickLabelInterval: (_value: string, index: number) => index % step === 0,
  }
}
